import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { supabase } from '../../utils/supabase';
import { CropVariety, CropVarietyState } from '../../types/cropVarieties';

export const fetchCropVarieties = createAsyncThunk(
  'cropVarieties/fetchCropVarieties',
  async (_, thunkAPI) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const { data, error } = await supabase
        .from('crop_varieties')
        .select('*')
        .eq('user_id', user.id)
        .order('name', { ascending: true });

      if (error) throw error;
      return data as CropVariety[];
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const addCropVariety = createAsyncThunk(
  'cropVarieties/addCropVariety',
  async (variety: Omit<CropVariety, 'id' | 'created_at' | 'user_id'>, thunkAPI) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const { data, error } = await supabase
        .from('crop_varieties')
        .insert([{ ...variety, user_id: user.id }])
        .select()
        .single();

      if (error) throw error;
      return data as CropVariety;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updateCropVariety = createAsyncThunk(
  'cropVarieties/updateCropVariety',
  async ({ id, ...updates }: Partial<CropVariety> & { id: string }, thunkAPI) => {
    try {
      const { data, error } = await supabase
        .from('crop_varieties')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as CropVariety;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteCropVariety = createAsyncThunk(
  'cropVarieties/deleteCropVariety',
  async (id: string, thunkAPI) => {
    try {
      // First check if variety is used in any crops
      const { data: crops, error: cropsError } = await supabase
        .from('crops')
        .select('id, name')
        .eq('variety_id', id);

      if (cropsError) throw cropsError;

      if (crops && crops.length > 0) {
        throw new Error(JSON.stringify({
          message: 'Cannot delete variety - it is being used by crops',
          crops: crops.map(c => ({ id: c.id, name: c.name }))
        }));
      }

      // If no crops found, proceed with deletion
      const { error } = await supabase
        .from('crop_varieties')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.message.startsWith('{') ? error.message : 'Failed to delete variety'
      );
    }
  }
);

const initialState: CropVarietyState = {
  varieties: [],
  loading: false,
  error: null,
};

const cropVarietiesSlice = createSlice({
  name: 'cropVarieties',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCropVarieties.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCropVarieties.fulfilled, (state, action) => {
        state.loading = false;
        state.varieties = action.payload;
        state.error = null;
      })
      .addCase(fetchCropVarieties.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(addCropVariety.fulfilled, (state, action) => {
        state.varieties.push(action.payload);
        state.varieties.sort((a, b) => a.name.localeCompare(b.name));
      })
      .addCase(updateCropVariety.fulfilled, (state, action) => {
        const index = state.varieties.findIndex(v => v.id === action.payload.id);
        if (index !== -1) {
          state.varieties[index] = action.payload;
        }
        state.varieties.sort((a, b) => a.name.localeCompare(b.name));
      })
      .addCase(deleteCropVariety.fulfilled, (state, action) => {
        state.varieties = state.varieties.filter(v => v.id !== action.payload);
      });
  },
});

export const { clearError } = cropVarietiesSlice.actions;
export default cropVarietiesSlice.reducer;